import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { KpiCard } from '@/shared/ui/KpiCard';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import type {
  BureauProduct,
  CallKind,
  CallResult,
  ConsumptionFilters,
  ConsumptionLine,
  ConsumptionReport,
} from './api';
import {
  hojeEmBrasilia,
  inicioDoMesEmBrasilia,
  produtos,
  rotuloDoAmbiente,
  rotuloDoFaturamento,
  rotuloDoProduto,
  rotuloDoResultado,
  rotuloDoTipo,
  tempoDeResposta,
} from './labels';

const limite = 50;

interface Recorte {
  rotulo: string;
  valor: string;
}

/**
 * Livro de consumo das consultas ao bureau: filtros, totais e chamadas.
 *
 * Serve ao relatorio da empresa e ao da plataforma, que difere so na fonte e na coluna
 * da empresa. Cada linha e uma chamada ao fornecedor, a autenticacao inclusive. O custo
 * desconhecido (sem preco cadastrado no instante) aparece como tal, e nunca como zero.
 */
export function ConsumptionReportView({
  chave,
  carregar,
  comEmpresa = false,
  extraFiltros,
}: {
  chave: string;
  carregar: (
    filtros: ConsumptionFilters,
    signal: AbortSignal,
  ) => Promise<ConsumptionReport>;
  comEmpresa?: boolean;
  /** Filtros proprios da tela (a empresa, na plataforma), que entram na consulta. */
  extraFiltros?: { filtros: ConsumptionFilters; campos: ReactNode };
}) {
  const [de, setDe] = useState(inicioDoMesEmBrasilia());
  const [ate, setAte] = useState(hojeEmBrasilia());
  const [produto, setProduto] = useState<BureauProduct | ''>('');
  const [resultado, setResultado] = useState<CallResult | ''>('');
  const [tipo, setTipo] = useState<CallKind | ''>('');
  const [tomador, setTomador] = useState<Recorte | null>(null);
  const [pessoa, setPessoa] = useState<Recorte | null>(null);
  const [offset, setOffset] = useState(0);

  const filtros: ConsumptionFilters = {
    from: de,
    to: ate,
    product: produto,
    result: resultado,
    kind: tipo,
    limit: limite,
    offset,
    ...(tomador ? { policyholder_id: tomador.valor } : {}),
    ...(pessoa ? { requested_by: pessoa.valor } : {}),
    ...(extraFiltros?.filtros ?? {}),
  };

  const consulta = useQuery({
    queryKey: ['consumo-de-consultas', chave, filtros],
    queryFn: ({ signal }) => carregar(filtros, signal),
  });

  const recomecar = (acao: () => void) => {
    acao();
    setOffset(0);
  };

  const colunas: Column<ConsumptionLine>[] = [
    {
      key: 'quando',
      header: 'Data e hora',
      render: (linha) => (
        <span className="tabular-nums">{formatDateTime(linha.started_at)}</span>
      ),
    },
    ...(comEmpresa
      ? [
          {
            key: 'empresa',
            header: 'Empresa',
            render: (linha: ConsumptionLine) => (
              <span className="text-ink-900">
                {linha.tenant_name || linha.tenant_id}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'chamada',
      header: 'Chamada',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">
            {linha.kind === 'consulta' && linha.product
              ? rotuloDoProduto[linha.product]
              : rotuloDoTipo[linha.kind]}
          </p>
          <p className="truncate text-2xs text-ink-500">
            {linha.kind === 'autenticacao'
              ? 'Nao tarifada'
              : `Etapa ${linha.stage === 'master' ? 'Master' : 'basica'}`}
          </p>
        </div>
      ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      hideBelow: 'md',
      render: (linha) => (
        <button
          type="button"
          className="truncate text-left text-brand-600 hover:underline"
          title="Filtrar por este tomador"
          onClick={() =>
            recomecar(() =>
              setTomador({
                rotulo: linha.policyholder_label,
                valor: linha.policyholder_id,
              }),
            )
          }
        >
          {linha.policyholder_label || linha.policyholder_id}
        </button>
      ),
    },
    {
      key: 'pessoa',
      header: 'Quem pediu',
      hideBelow: 'lg',
      render: (linha) =>
        linha.requested_by ? (
          <button
            type="button"
            className="truncate text-left text-brand-600 hover:underline"
            title="Filtrar por quem pediu"
            onClick={() =>
              recomecar(() =>
                setPessoa({
                  rotulo: linha.requested_label,
                  valor: linha.requested_by ?? '',
                }),
              )
            }
          >
            {linha.requested_label}
          </button>
        ) : (
          <span className="text-ink-500">{linha.requested_label || 'Sistema'}</span>
        ),
    },
    {
      key: 'ambiente',
      header: 'Ambiente',
      hideBelow: 'lg',
      render: (linha) =>
        linha.simulated ? (
          <Badge tone="warning">SIMULADO</Badge>
        ) : (
          <span>{rotuloDoAmbiente[linha.environment] ?? linha.environment}</span>
        ),
    },
    {
      key: 'tempo',
      header: 'Tempo',
      align: 'right',
      hideBelow: 'md',
      render: (linha) => (
        <span className="tabular-nums">
          {tempoDeResposta(linha.duration_ms)}
          {linha.http_status ? ` (HTTP ${linha.http_status})` : ''}
        </span>
      ),
    },
    {
      key: 'resultado',
      header: 'Resultado',
      render: (linha) => {
        const rotulo = rotuloDoResultado[linha.result];
        return (
          <div className="flex flex-col items-start gap-0.5">
            <Badge tone={rotulo.tom}>{rotulo.rotulo}</Badge>
            <span className="text-2xs text-ink-500">
              {rotuloDoFaturamento[linha.billable]}
            </span>
          </div>
        );
      },
    },
    {
      key: 'custo',
      header: 'Custo',
      align: 'right',
      render: (linha) =>
        linha.cost ? (
          <span className="tabular-nums">{formatMoney(linha.cost)}</span>
        ) : (
          <span className="text-ink-500">Desconhecido</span>
        ),
    },
  ];

  const totais = consulta.data?.totals;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <div className="grid gap-3 border-b border-line px-5 py-4 sm:grid-cols-2 lg:grid-cols-6">
          <Field
            label="De"
            type="date"
            value={de}
            onChange={(evento) => recomecar(() => setDe(evento.target.value))}
          />
          <Field
            label="Ate"
            type="date"
            value={ate}
            onChange={(evento) => recomecar(() => setAte(evento.target.value))}
          />
          <Select
            label="Produto"
            placeholder="Todos"
            value={produto}
            options={produtos.map((p) => ({ value: p, label: rotuloDoProduto[p] }))}
            onChange={(evento) =>
              recomecar(() => setProduto(evento.target.value as BureauProduct | ''))
            }
          />
          <Select
            label="Resultado"
            placeholder="Todos"
            value={resultado}
            options={(Object.keys(rotuloDoResultado) as CallResult[]).map((r) => ({
              value: r,
              label: rotuloDoResultado[r].rotulo,
            }))}
            onChange={(evento) =>
              recomecar(() => setResultado(evento.target.value as CallResult | ''))
            }
          />
          <Select
            label="Tipo de chamada"
            placeholder="Todos"
            value={tipo}
            options={(Object.keys(rotuloDoTipo) as CallKind[]).map((k) => ({
              value: k,
              label: rotuloDoTipo[k],
            }))}
            onChange={(evento) =>
              recomecar(() => setTipo(evento.target.value as CallKind | ''))
            }
          />
          {extraFiltros?.campos}
        </div>
        {tomador || pessoa ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-2 text-xs">
            {tomador ? (
              <Recortado
                rotulo={`Tomador: ${tomador.rotulo}`}
                onLimpar={() => recomecar(() => setTomador(null))}
              />
            ) : null}
            {pessoa ? (
              <Recortado
                rotulo={`Quem pediu: ${pessoa.rotulo}`}
                onLimpar={() => recomecar(() => setPessoa(null))}
              />
            ) : null}
          </div>
        ) : null}
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Consultas"
          icon="chart"
          value={String(totais?.consultations ?? 0)}
          hint={`${totais?.authentications ?? 0} autenticacoes, nao tarifadas`}
          loading={consulta.isPending}
        />
        <KpiCard
          label="Custo conhecido"
          icon="coins"
          value={formatMoney(totais?.known_cost) || 'R$ 0,00'}
          hint="Pelo preco da plataforma no instante de cada chamada"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Custo desconhecido"
          icon="alert"
          tone={totais && totais.unknown_cost_calls > 0 ? 'warning' : 'neutral'}
          value={`${totais?.unknown_cost_calls ?? 0} chamadas`}
          hint="Sem preco cadastrado no instante; nao somadas como zero"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Faturamento incerto"
          icon="clock"
          tone={totais && totais.billable_unknown_calls > 0 ? 'warning' : 'neutral'}
          value={`${totais?.billable_unknown_calls ?? 0} chamadas`}
          hint="Falharam depois de sair; podem ter sido cobradas"
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <div className="flex items-center border-b border-line px-5 py-3">
          <span className="text-xs text-ink-600">
            {consulta.data?.total ?? 0} chamadas no periodo
          </span>
        </div>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o consumo">
            {consulta.error.message}
          </Alert>
        ) : null}
        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(linha) => linha.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="chart"
              title="Nenhuma chamada no periodo"
              description="Cada chamada ao fornecedor, a autenticacao inclusive, aparece aqui."
            />
          }
        />
        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}

function Recortado({ rotulo, onLimpar }: { rotulo: string; onLimpar: () => void }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface-sunken px-3 py-1">
      {rotulo}
      <Button
        size="sm"
        variant="ghost"
        onClick={onLimpar}
        aria-label={`Limpar ${rotulo}`}
      >
        Limpar
      </Button>
    </span>
  );
}
